import { Box, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { StateAbbreviation } from '@repo/supergraph/marketplace/types';
import { MobileDialog } from '@repo/ui/OffersFilter/MobileDialog';
import { MobileFilterSortButton } from '@repo/ui/OffersFilter/MobileFilterSortButton';
import { SortByMobile } from '@repo/ui/OffersFilter/SortByMobile';
import { startTransition, useMemo, useState } from 'react';
import { useShallow } from 'zustand/shallow';
import { getFilterConfig } from '../DesktopFilterBar/filterConfig';
import { SelectedFilterPills } from '../SelectedFilterPills';
import { useOffersFilterStore } from '../store';
import { MobileSelectBonus } from './MobileSelectBonus';
import { MobileSelectLocation } from './MobileSelectLocation';
import { MobileSelectMinBalance } from './MobileSelectMinBalance';
import { MobileSelectMinDeposit } from './MobileSelectMinDeposit';
import { MobileSelectProductType } from './MobileSelectProductType';
import { MobileSelectTermLength } from './MobileSelectTermLength';

export const MobileFilters = ({
  slug,
  initialLocation,
  analyticsProps,
  onLocationReset,
  initialClientRegion,
  regionCode,
  isBankBonusesFilterExperiment,
}: {
  slug: string;
  initialLocation?: StateAbbreviation;
  analyticsProps: {
    actionSection: string;
    pageIdentifier: string;
  };
  onLocationReset?: () => void;
  initialClientRegion?: string;
  regionCode?: StateAbbreviation;
  isBankBonusesFilterExperiment?: boolean;
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleOpenCloseDialog = () => {
    startTransition(() => {
      setIsDialogOpen(isOpen => !isOpen);
    });
  };

  const { appliedFilters, sortOptions } = useOffersFilterStore(
    useShallow(state => ({
      appliedFilters: state.appliedFilters,
      sortOptions: state.sortOptions,
    })),
  );

  const { filterPillsToDisplay } = useMemo(() => {
    const sortedFilters = [
      ...(appliedFilters?.selectedProductTypes || []),
      ...(appliedFilters?.selectedMinDepositRanges || []),
      ...(appliedFilters?.selectedTermRanges || []),
      ...(appliedFilters?.selectedMinBalanceRanges || []),
      ...(appliedFilters?.selectedBonusOptions || []),
    ];
    if (
      appliedFilters?.selectedLocation &&
      appliedFilters.selectedLocation !== initialClientRegion
    ) {
      sortedFilters.push(appliedFilters.selectedLocation);
    }

    return {
      filterPillsToDisplay: sortedFilters.length,
    };
  }, [
    appliedFilters?.selectedProductTypes,
    appliedFilters?.selectedLocation,
    appliedFilters?.selectedMinDepositRanges,
    appliedFilters?.selectedTermRanges,
    appliedFilters?.selectedMinBalanceRanges,
    appliedFilters?.selectedBonusOptions,
  ]);

  const renderFilter = (componentName: string) => {
    switch (componentName) {
      case 'ProductTypeSelect':
        return (
          <MobileSelectProductType
            key={componentName}
            regionCode={regionCode}
            {...analyticsProps}
          />
        );
      case 'LocationSelect':
        if (!initialLocation) {
          return null;
        }
        return <MobileSelectLocation key={componentName} {...analyticsProps} />;
      case 'TermLengthSelect':
        return <MobileSelectTermLength key={componentName} {...analyticsProps} />;
      case 'MinDepositSelect':
        return <MobileSelectMinDeposit key={componentName} {...analyticsProps} />;
      case 'MinBalanceSelect':
        return <MobileSelectMinBalance key={componentName} {...analyticsProps} />;
      case 'BonusSelect':
        return <MobileSelectBonus key={componentName} {...analyticsProps} />;
      default:
        return null;
    }
  };

  const filterConfig = getFilterConfig(slug);

  return (
    <Box
      sx={{ display: { xs: 'flex', sm: 'none' } }}
      component="section"
      aria-labelledby="mobile-page-filters"
    >
      <Typography id="mobile-page-filters" component="h3" sx={visuallyHidden}>
        Filters
      </Typography>
      <MobileFilterSortButton
        handleOpenCloseDialog={handleOpenCloseDialog}
        isBankBonusesFilterExperiment={isBankBonusesFilterExperiment}
      />
      <MobileDialog
        handleOpenCloseDialog={handleOpenCloseDialog}
        isDialogOpen={isDialogOpen}
        dialogId={`${slug}-offers-filters`}
        useStore={useOffersFilterStore}
        filterPillsToDisplay={filterPillsToDisplay}
        actionSection={analyticsProps.actionSection}
        pageIdentifier={analyticsProps.pageIdentifier}
        onLocationReset={onLocationReset}
      >
        <Box sx={{ overflowY: 'auto', p: 3, mb: '80px' }}>
          <Box sx={{ pb: '2rem' }}>
            <SelectedFilterPills
              isMobile={true}
              analyticsProps={analyticsProps}
              regionCode={regionCode}
            />
          </Box>
          {sortOptions.length > 0 && (
            <SortByMobile
              sortOptions={sortOptions}
              useStore={useOffersFilterStore}
              actionSection={analyticsProps.actionSection}
              pageIdentifier={analyticsProps.pageIdentifier}
            />
          )}
          {filterConfig.filters.map(({ component }) => renderFilter(component))}
        </Box>
      </MobileDialog>
    </Box>
  );
};

export { MobileFiltersWithGeo } from './MobileFiltersWithGeo';
